import type { ChromeCopy } from "./uiText";

/**
 * Chrome-ul widgetului în limbile peste ro/en (es, de, it, fr, pt). Separat de
 * `uiText.ts` doar ca fișierul acela să rămână citibil: aceeași formă, aceleași
 * câmpuri, același rol - tot ce scriem NOI și citește vizitatorul.
 *
 * Alese pentru siturile pe care le avem în față (Tenerife, Europa de Vest): un
 * vizitator pe un sit în spaniolă primea salutul în engleză (regresia 30 oct
 * 2026, D12a). Orice altă limbă cade pe engleză, ca înainte.
 */
export const OTHER_CHROME_COPY: Record<"es" | "de" | "it" | "fr" | "pt", ChromeCopy> = {
  es: {
    assistantName: "AI Concierge",
    close: "Cerrar el asistente",
    open: "Abrir el asistente",
    placeholder: "Pregúntame algo…",
    send: "Enviar",
    stop: "Detener la respuesta",
    mic: "Dictar tu mensaje",
    micListening: "Te escucho… pulsa de nuevo para parar",
    thinking: "El asistente está pensando",
    orbAsk: "Pregunta a este sitio",
    orbPlaceholder: "Pregunta lo que quieras sobre este sitio…",
    historyLabel: "Historial de la conversación",
    historyShow: "Mostrar la conversación",
    historyHide: "Ocultar la conversación",
    thinkingLabel: "… pensando …",
    voiceStart: "Hablar con el asistente",
    voiceEnd: "Terminar la conversación de voz",
    voiceStartShort: "Hablar",
    voiceEndShort: "Parar conversación",
    micDenied: "Se denegó el acceso al micrófono. Permítelo en el navegador e inténtalo de nuevo.",
    sensitiveRedacted:
      "Los datos sensibles (números de tarjeta, correos, contraseñas) se eliminaron automáticamente antes del envío: aquí nunca hacen falta.",
    micNoSpeech: "No he oído nada: revisa el micrófono e inténtalo de nuevo.",
    micNetwork: "El servicio de dictado de tu navegador no responde.",
    micFailed: "El dictado se detuvo de forma inesperada. Inténtalo de nuevo.",
    voiceNotConfigured: "La voz aún no está disponible aquí.",
    voiceMicBlocked:
      "El micrófono está bloqueado para este sitio. Permítelo desde el icono a la izquierda de la barra de direcciones y recarga la página.",
    voiceNoMic: "No se encontró ningún micrófono en este dispositivo.",
    voiceFailed: "No se pudo iniciar la conversación de voz. Inténtalo de nuevo.",
    voiceBusy: "Todas las líneas de voz están ocupadas ahora. Escríbeme aquí mientras tanto: respondo igual de rápido.",
    voiceResumeTap: "La conversación de voz está en pausa: toca el micrófono para continuar donde lo dejamos.",
    voiceMinutesOver: "Se acabaron los minutos de voz: seguimos aquí, por escrito. Nuestra conversación ya está en el hilo.",
    voiceInAppBrowser:
      "Esta ventana dentro de la app no tiene acceso al micrófono. Abre el enlace en el navegador del móvil (Safari o Chrome) y la voz funcionará.",
    greeting: "¡Hola! Puedo ver lo que estás haciendo: dime qué buscas y te guío.",
    askFailed: "Algo ha fallado. Inténtalo de nuevo.",
    targetMissing: "No encuentro ese elemento en esta página. ¿Estás en la página correcta?",
  },
  de: {
    assistantName: "AI Concierge",
    close: "Assistenten schließen",
    open: "Assistenten öffnen",
    placeholder: "Frag mich etwas…",
    send: "Senden",
    stop: "Antwort stoppen",
    mic: "Nachricht diktieren",
    micListening: "Ich höre zu… erneut tippen zum Beenden",
    thinking: "Der Assistent denkt nach",
    orbAsk: "Diese Website fragen",
    orbPlaceholder: "Frag mich alles zu dieser Website…",
    historyLabel: "Gesprächsverlauf",
    historyShow: "Gespräch anzeigen",
    historyHide: "Gespräch ausblenden",
    thinkingLabel: "… denke nach …",
    voiceStart: "Mit dem Assistenten sprechen",
    voiceEnd: "Sprachgespräch beenden",
    voiceStartShort: "Sprechen",
    voiceEndShort: "Gespräch beenden",
    micDenied: "Der Mikrofonzugriff wurde verweigert. Erlaube ihn im Browser und versuche es erneut.",
    sensitiveRedacted:
      "Sensible Angaben (Kartennummern, E-Mail-Adressen, Passwörter) wurden vor dem Senden automatisch entfernt: Sie werden hier nie gebraucht.",
    micNoSpeech: "Ich habe nichts gehört: Prüfe das Mikrofon und versuche es erneut.",
    micNetwork: "Der Diktierdienst deines Browsers antwortet nicht.",
    micFailed: "Das Diktat wurde unerwartet beendet. Bitte erneut versuchen.",
    voiceNotConfigured: "Sprache ist hier noch nicht verfügbar.",
    voiceMicBlocked:
      "Das Mikrofon ist für diese Website blockiert. Erlaube es über das Symbol links neben der Adressleiste und lade die Seite neu.",
    voiceNoMic: "Auf diesem Gerät wurde kein Mikrofon gefunden.",
    voiceFailed: "Das Sprachgespräch konnte nicht gestartet werden. Bitte erneut versuchen.",
    voiceBusy: "Alle Sprachleitungen sind gerade belegt. Schreib mir solange hier: Ich antworte genauso schnell.",
    voiceResumeTap: "Das Sprachgespräch ist pausiert: Tippe auf das Mikrofon, um dort weiterzumachen, wo wir aufgehört haben.",
    voiceMinutesOver: "Die Sprachminuten sind aufgebraucht: Wir machen hier schriftlich weiter. Unser Gespräch steht schon im Verlauf.",
    voiceInAppBrowser:
      "Dieses In-App-Fenster hat keinen Mikrofonzugriff. Öffne den Link im Browser deines Telefons (Safari oder Chrome), dann funktioniert die Sprache.",
    greeting: "Hallo! Ich sehe, was du gerade tust: Sag mir, was du suchst, und ich führe dich hin.",
    askFailed: "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
    targetMissing: "Ich finde dieses Element auf dieser Seite nicht. Bist du auf der richtigen Seite?",
  },
  it: {
    assistantName: "AI Concierge",
    close: "Chiudi l'assistente",
    open: "Apri l'assistente",
    placeholder: "Chiedimi qualcosa…",
    send: "Invia",
    stop: "Interrompi la risposta",
    mic: "Detta il tuo messaggio",
    micListening: "Ti ascolto… tocca di nuovo per fermare",
    thinking: "L'assistente sta pensando",
    orbAsk: "Chiedi a questo sito",
    orbPlaceholder: "Chiedimi qualsiasi cosa su questo sito…",
    historyLabel: "Cronologia della conversazione",
    historyShow: "Mostra la conversazione",
    historyHide: "Nascondi la conversazione",
    thinkingLabel: "… sto pensando …",
    voiceStart: "Parla con l'assistente",
    voiceEnd: "Termina la conversazione vocale",
    voiceStartShort: "Parla",
    voiceEndShort: "Ferma conversazione",
    micDenied: "L'accesso al microfono è stato negato. Consentilo dal browser e riprova.",
    sensitiveRedacted:
      "I dati sensibili (numeri di carta, email, password) sono stati rimossi automaticamente prima dell'invio: qui non servono mai.",
    micNoSpeech: "Non ho sentito nulla: controlla il microfono e riprova.",
    micNetwork: "Il servizio di dettatura del tuo browser non risponde.",
    micFailed: "La dettatura si è interrotta inaspettatamente. Riprova.",
    voiceNotConfigured: "La voce non è ancora disponibile qui.",
    voiceMicBlocked:
      "Il microfono è bloccato per questo sito. Consentilo dall'icona a sinistra della barra degli indirizzi e ricarica la pagina.",
    voiceNoMic: "Nessun microfono trovato su questo dispositivo.",
    voiceFailed: "Non è stato possibile avviare la conversazione vocale. Riprova.",
    voiceBusy: "Tutte le linee vocali sono occupate al momento. Scrivimi qui nel frattempo: rispondo altrettanto in fretta.",
    voiceResumeTap: "La conversazione vocale è in pausa: tocca il microfono per riprendere da dove eravamo rimasti.",
    voiceMinutesOver: "I minuti vocali sono finiti: continuiamo qui, per iscritto. La nostra conversazione è già nel thread.",
    voiceInAppBrowser:
      "Questa finestra interna all'app non ha accesso al microfono. Apri il link nel browser del telefono (Safari o Chrome) e la voce funzionerà.",
    greeting: "Ciao! Vedo cosa stai facendo: dimmi cosa cerchi e ti guido io.",
    askFailed: "Qualcosa è andato storto. Riprova.",
    targetMissing: "Non trovo quell'elemento in questa pagina. Sei sulla pagina giusta?",
  },
  fr: {
    assistantName: "AI Concierge",
    close: "Fermer l'assistant",
    open: "Ouvrir l'assistant",
    placeholder: "Posez-moi une question…",
    send: "Envoyer",
    stop: "Arrêter la réponse",
    mic: "Dicter votre message",
    micListening: "Je vous écoute… appuyez à nouveau pour arrêter",
    thinking: "L'assistant réfléchit",
    orbAsk: "Interroger ce site",
    orbPlaceholder: "Posez-moi n'importe quelle question sur ce site…",
    historyLabel: "Historique de la conversation",
    historyShow: "Afficher la conversation",
    historyHide: "Masquer la conversation",
    thinkingLabel: "… je réfléchis …",
    voiceStart: "Parler à l'assistant",
    voiceEnd: "Terminer la conversation vocale",
    voiceStartShort: "Parler",
    voiceEndShort: "Arrêter",
    micDenied: "L'accès au micro a été refusé. Autorisez-le dans votre navigateur et réessayez.",
    sensitiveRedacted:
      "Les données sensibles (numéros de carte, e-mails, mots de passe) ont été supprimées automatiquement avant l'envoi : elles ne sont jamais nécessaires ici.",
    micNoSpeech: "Je n'ai rien entendu : vérifiez votre micro et réessayez.",
    micNetwork: "Le service de dictée de votre navigateur ne répond pas.",
    micFailed: "La dictée s'est arrêtée de façon inattendue. Réessayez.",
    voiceNotConfigured: "La voix n'est pas encore disponible ici.",
    voiceMicBlocked:
      "Le micro est bloqué pour ce site. Autorisez-le depuis l'icône à gauche de la barre d'adresse, puis rechargez la page.",
    voiceNoMic: "Aucun micro n'a été trouvé sur cet appareil.",
    voiceFailed: "La conversation vocale n'a pas pu démarrer. Réessayez.",
    voiceBusy: "Toutes les lignes vocales sont occupées. Écrivez-moi ici en attendant : je réponds tout aussi vite.",
    voiceResumeTap: "La conversation vocale est en pause : touchez le micro pour reprendre là où nous en étions.",
    voiceMinutesOver: "Les minutes vocales sont épuisées : continuons ici, par écrit. Notre conversation est déjà dans le fil.",
    voiceInAppBrowser:
      "Cette fenêtre intégrée à l'application n'a pas accès au micro. Ouvrez le lien dans le navigateur du téléphone (Safari ou Chrome) et la voix fonctionnera.",
    greeting: "Bonjour ! Je vois ce que vous faites : dites-moi ce que vous cherchez et je vous guide.",
    askFailed: "Un problème est survenu. Réessayez.",
    targetMissing: "Je ne trouve pas cet élément sur cette page. Êtes-vous sur la bonne page ?",
  },
  pt: {
    assistantName: "AI Concierge",
    close: "Fechar o assistente",
    open: "Abrir o assistente",
    placeholder: "Pergunte-me algo…",
    send: "Enviar",
    stop: "Parar a resposta",
    mic: "Ditar a sua mensagem",
    micListening: "Estou a ouvir… toque de novo para parar",
    thinking: "O assistente está a pensar",
    orbAsk: "Pergunte a este site",
    orbPlaceholder: "Pergunte qualquer coisa sobre este site…",
    historyLabel: "Histórico da conversa",
    historyShow: "Mostrar a conversa",
    historyHide: "Ocultar a conversa",
    thinkingLabel: "… a pensar …",
    voiceStart: "Falar com o assistente",
    voiceEnd: "Terminar a conversa por voz",
    voiceStartShort: "Falar",
    voiceEndShort: "Parar conversa",
    micDenied: "O acesso ao microfone foi negado. Permita-o no navegador e tente de novo.",
    sensitiveRedacted:
      "Os dados sensíveis (números de cartão, e-mails, palavras-passe) foram removidos automaticamente antes do envio: nunca são necessários aqui.",
    micNoSpeech: "Não ouvi nada: verifique o microfone e tente de novo.",
    micNetwork: "O serviço de ditado do seu navegador não responde.",
    micFailed: "O ditado parou inesperadamente. Tente de novo.",
    voiceNotConfigured: "A voz ainda não está disponível aqui.",
    voiceMicBlocked:
      "O microfone está bloqueado para este site. Permita-o a partir do ícone à esquerda da barra de endereços e recarregue a página.",
    voiceNoMic: "Não foi encontrado nenhum microfone neste dispositivo.",
    voiceFailed: "Não foi possível iniciar a conversa por voz. Tente de novo.",
    voiceBusy: "Todas as linhas de voz estão ocupadas agora. Escreva-me aqui entretanto: respondo igualmente depressa.",
    voiceResumeTap: "A conversa por voz está em pausa: toque no microfone para continuar de onde ficámos.",
    voiceMinutesOver: "Os minutos de voz acabaram: continuamos aqui, por escrito. A nossa conversa já está no fio.",
    voiceInAppBrowser:
      "Esta janela dentro da aplicação não tem acesso ao microfone. Abra o link no navegador do telemóvel (Safari ou Chrome) e a voz vai funcionar.",
    greeting: "Olá! Vejo o que está a fazer: diga-me o que procura e eu guio-o.",
    askFailed: "Algo correu mal. Tente de novo.",
    targetMissing: "Não encontro esse elemento nesta página. Está na página certa?",
  },
};
